import { useMutation, useQueryClient } from '@tanstack/react-query';

import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { vestigasApi } from '~/services/kyClient';

import { queryKeysOrganizationalUnit } from './queryKeys';

type OrganizationalUnitData = {
  name: string;
  parentOus: UUID[];
  accountingReferences?: UUID[];
  childOus?: UUID[];
  companies?: UUID[];
  sites?: UUID[];
  syncKey?: string;
  userGroups?: UUID[];
  users?: UUID[];
  vehicles?: UUID[];
};

type OrganizationalUnitCreateResponse = {
  code: number;
  message: string;
  id: UUID;
};

/**
 * Creates a new organizational unit.
 *
 * @param {OrganizationalUnitData} organizationalUnitData - An object containing the organizational unit data.
 * @returns {Promise} A promise that resolves with the API response.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Access-Control/operation/create_organizational_unit_access_control_org_unit_post
 */
export const createOrganizationalUnit = async (
  organizationalUnitData: OrganizationalUnitData,
) => {
  const response = await vestigasApi
    .post(ENDPOINT.ORGANIZATIONAL_UNIT.CREATE(), {
      json: organizationalUnitData,
    })
    .json<OrganizationalUnitCreateResponse>();

  return response;
};

/**
 * Custom hook for creating a new organizational unit.
 *
 * @returns {Object} An object containing the mutation function and related properties.
 */
export const useMutationCreateOrganizationalUnit = (
  options?: Parameters<typeof useMutation>[0],
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (organizationalUnitData: OrganizationalUnitData) =>
      createOrganizationalUnit(organizationalUnitData),
    onSuccess() {
      queryClient.invalidateQueries({
        queryKey: queryKeysOrganizationalUnit.getAll({}),
      });
    },
    ...options,
  });
};
